import { useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';
import {
  getCatalogEntityRef,
  toCatalogHomeItem,
  type CatalogHomeEntity,
  type CatalogHomeItem,
} from './catalogHomeCard';
import LaunchIcon from '@material-ui/icons/Launch';

const PART_OF = 'partOf';
const UNTYPED = 'other';

const toTenantHomeItem = (tenant: CatalogHomeEntity): CatalogHomeItem =>
  toCatalogHomeItem(
    tenant,
    'tenant',
    tenant.metadata.description ??
      (tenant.spec?.owner ? `owner: ${tenant.spec.owner}` : undefined),
  );

type TypeGroup = {
  type: string;
  systems: CatalogHomeItem[];
};

type DomainGroup = {
  ref: string;
  item: CatalogHomeItem;
  typeGroups: TypeGroup[];
  totalSystems: number;
};

const compareByTitle = (a: { title: string }, b: { title: string }) =>
  a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });

export const TenantSection = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const [tenants, setTenants] = useState<CatalogHomeItem[]>([]);
  const [domainEntities, setDomainEntities] = useState<CatalogHomeEntity[]>([]);
  const [systemEntities, setSystemEntities] = useState<CatalogHomeEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [selectedTenantRef, setSelectedTenantRef] = useState<string | null>(
    null,
  );
  const [expandedDomainRefs, setExpandedDomainRefs] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    let isMounted = true;

    const fetchEntities = async (
      kind: string,
      ownershipEntityRefs: string[],
    ): Promise<CatalogHomeEntity[]> => {
      const catalogBaseUrl = await discoveryApi.getBaseUrl('catalog');

      /*
       * Stesso "cono di visibilità" del filtro Personal/Owned
       * della pagina Catalog: solo entità di proprietà dell'utente
       * o di uno dei suoi gruppi (relations.ownedBy in OR).
       */
      const filterValue = [
        `kind=${kind}`,
        ...ownershipEntityRefs.map(ref => `relations.ownedBy=${ref}`),
      ].join(',');

      const response = await fetchApi.fetch(
        `${catalogBaseUrl}/entities?${new URLSearchParams({
          filter: filterValue,
        }).toString()}`,
      );

      const contentType = response.headers.get('content-type') ?? '';

      if (!response.ok) {
        throw new Error(`Unable to load ${kind} entities (${response.status})`);
      }

      if (!contentType.includes('application/json')) {
        throw new Error('Catalog returned a non-JSON response.');
      }

      return (await response.json()) as CatalogHomeEntity[];
    };

    const loadAll = async () => {
      try {
        const { ownershipEntityRefs } =
          await identityApi.getBackstageIdentity();

        const [tenantEntities, domains, systems] = await Promise.all([
          fetchEntities('tenant', ownershipEntityRefs),
          fetchEntities('domain', ownershipEntityRefs),
          fetchEntities('system', ownershipEntityRefs),
        ]);

        const tenantItems = tenantEntities
          .map(toTenantHomeItem)
          .sort(compareByTitle);

        if (isMounted) {
          setTenants(tenantItems);
          setDomainEntities(domains);
          setSystemEntities(systems);
          setError(undefined);
        }
      } catch (e) {
        if (isMounted) {
          setError(e instanceof Error ? e.message : 'Unable to load tenants');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadAll();

    return () => {
      isMounted = false;
    };
  }, [discoveryApi, fetchApi, identityApi]);

  const domainGroupsByTenantRef = useMemo(() => {
    const systemsByDomainRef = new Map<string, CatalogHomeItem[]>();

    for (const system of systemEntities) {
      const domainRelation = system.relations?.find(
        relation =>
          relation.type === PART_OF && relation.targetRef.startsWith('domain:'),
      );

      if (!domainRelation) {
        continue;
      }

      const item = toCatalogHomeItem(
        system,
        'system',
        system.metadata.description,
      );
      const list = systemsByDomainRef.get(domainRelation.targetRef) ?? [];
      list.push(item);
      systemsByDomainRef.set(domainRelation.targetRef, list);
    }

    const groupsByTenantRef = new Map<string, DomainGroup[]>();

    for (const domain of domainEntities) {
      const tenantRelation = domain.relations?.find(
        relation =>
          relation.type === PART_OF && relation.targetRef.startsWith('tenant:'),
      );

      if (!tenantRelation) {
        continue;
      }

      const domainRef = getCatalogEntityRef(domain, 'domain');
      const systems = systemsByDomainRef.get(domainRef) ?? [];

      const systemsByType = new Map<string, CatalogHomeItem[]>();
      for (const system of systems) {
        const type = system.type ?? UNTYPED;
        const list = systemsByType.get(type) ?? [];
        list.push(system);
        systemsByType.set(type, list);
      }

      const typeGroups: TypeGroup[] = Array.from(systemsByType.entries()).map(
        ([type, groupSystems]) => ({
          type,
          systems: groupSystems.sort(compareByTitle),
        }),
      );

      typeGroups.sort((a, b) =>
        a.type.localeCompare(b.type, undefined, { sensitivity: 'base' }),
      );

      const domainGroup: DomainGroup = {
        ref: domainRef,
        item: toCatalogHomeItem(domain, 'domain', domain.metadata.description),
        typeGroups,
        totalSystems: systems.length,
      };

      const list = groupsByTenantRef.get(tenantRelation.targetRef) ?? [];
      list.push(domainGroup);
      groupsByTenantRef.set(tenantRelation.targetRef, list);
    }

    for (const list of groupsByTenantRef.values()) {
      list.sort((a, b) => compareByTitle(a.item, b.item));
    }

    return groupsByTenantRef;
  }, [domainEntities, systemEntities]);

  const selectedDomainGroups = selectedTenantRef
    ? domainGroupsByTenantRef.get(selectedTenantRef) ?? []
    : [];

  useEffect(() => {
    if (selectedTenantRef) {
      const groups = domainGroupsByTenantRef.get(selectedTenantRef) ?? [];
      setExpandedDomainRefs(new Set(groups.map(group => group.ref)));
    } else {
      setExpandedDomainRefs(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantRef]);

  const handleTenantClick = (tenantRef: string) => {
    setSelectedTenantRef(prev => (prev === tenantRef ? null : tenantRef));
  };

  const toggleDomain = (domainRef: string) => {
    setExpandedDomainRefs(prev => {
      const next = new Set(prev);

      if (next.has(domainRef)) {
        next.delete(domainRef);
      } else {
        next.add(domainRef);
      }

      return next;
    });
  };

  return (
    <section className="rdap-home-tenants" aria-label="Tenants">
      <h2 className="rdap-home-tenants-title">Tenants</h2>

      {isLoading ? (
        <p className="rdap-home-systems-empty">Loading tenants...</p>
      ) : null}

      {!isLoading && error ? (
        <p className="rdap-home-systems-empty">{error}</p>
      ) : null}

      {!isLoading && !error && tenants.length === 0 ? (
        <p className="rdap-home-systems-empty">
          No Tenant entities found in catalog.
        </p>
      ) : null}

      {!isLoading && !error && tenants.length > 0 ? (
        <>
          <div className="rdap-home-tenant-marquee">
            <div className="rdap-home-tenant-row">
              {tenants.map(tenant => {
                const isSelected = tenant.entityRef === selectedTenantRef;

                return (
                  <div
                    key={tenant.entityRef}
                    className="rdap-home-tenant-shell"
                  >
                    <button
                      type="button"
                      className={`rdap-home-tenant-card${
                        isSelected ? ' is-selected' : ''
                      }`}
                      aria-label={`${
                        isSelected ? 'Hide' : 'Show'
                      } systems for ${tenant.title}`}
                      aria-pressed={isSelected}
                      data-entity-ref={tenant.entityRef}
                      title={tenant.title}
                      onClick={() => handleTenantClick(tenant.entityRef)}
                    >
                      {tenant.logoUrl ? (
                        <img
                          className="rdap-home-tenant-logo"
                          src={tenant.logoUrl}
                          alt={tenant.title}
                          draggable={false}
                        />
                      ) : (
                        <span className="rdap-home-tenant-fallback">
                          {tenant.initials}
                        </span>
                      )}
                    </button>
                    <Link
                      to={tenant.to}
                      className="rdap-home-entity-redirect"
                      aria-label={`Open tenant page ${tenant.title}`}
                      title={`Open ${tenant.title}`}
                      underline="none"
                    >
                      <LaunchIcon fontSize="small" />
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedTenantRef ? (
            <div className="rdap-home-tenant-panel">
              <hr className="rdap-home-divider" />

              {selectedDomainGroups.length === 0 ? (
                <p className="rdap-home-systems-empty">
                  No domains found for this tenant.
                </p>
              ) : (
                <div className="rdap-home-domain-groups">
                  {selectedDomainGroups.map(group => {
                    const isExpanded = expandedDomainRefs.has(group.ref);

                    return (
                      <div
                        className={`rdap-home-domain-group${
                          isExpanded ? ' is-expanded' : ''
                        }`}
                        key={group.ref}
                      >
                        <div className="rdap-home-domain-rail">
                          <button
                            type="button"
                            className="rdap-home-domain-toggle"
                            onClick={() => toggleDomain(group.ref)}
                            aria-expanded={isExpanded}
                            aria-label={`${
                              isExpanded ? 'Collapse' : 'Expand'
                            } ${group.item.title} domain`}
                          >
                            <span className="rdap-home-domain-toggle-label">
                              {group.item.title}
                            </span>
                          </button>
                        </div>

                        <div className="rdap-home-domain-content">
                          <div className="rdap-home-domain-summary">
                            {group.item.subtitle ? (
                              <span
                                className="rdap-home-domain-summary-description"
                                data-tooltip={group.item.subtitle}
                                aria-label={group.item.subtitle}
                              >
                                {group.item.subtitle}
                              </span>
                            ) : null}

                            <span className="rdap-home-domain-summary-count">
                              {group.totalSystems}{' '}
                              {group.totalSystems === 1 ? 'app' : 'apps'}
                            </span>

                            <span className="rdap-home-domain-summary-count">
                              {group.typeGroups.length}{' '}
                              {group.typeGroups.length === 1 ? 'type' : 'types'}
                            </span>

                            <Link
                              to={group.item.to}
                              className="rdap-home-entity-redirect rdap-home-domain-redirect"
                              aria-label={`Open domain page ${group.item.title}`}
                              title={`Open ${group.item.title}`}
                              underline="none"
                            >
                              <LaunchIcon fontSize="small" />
                            </Link>
                          </div>

                          {isExpanded ? (
                            group.typeGroups.length === 0 ? (
                              <p className="rdap-home-domain-empty">
                                No systems in this domain.
                              </p>
                            ) : (
                              <div className="rdap-home-type-groups">
                                {group.typeGroups.map(typeGroup => (
                                  <div
                                    className="rdap-home-type-group"
                                    key={typeGroup.type}
                                  >
                                    <div className="rdap-home-type-title">
                                      {typeGroup.type}
                                    </div>

                                    <div className="rdap-home-system-grid">
                                      {typeGroup.systems.map(system => (
                                        <Link
                                          key={system.entityRef}
                                          to={system.to}
                                          className="rdap-home-system-card"
                                          aria-label={`Open system ${system.title}`}
                                          data-entity-ref={system.entityRef}
                                          underline="none"
                                          title={system.title}
                                        >
                                          {system.logoUrl ? (
                                            <img
                                              className="rdap-home-system-logo"
                                              src={system.logoUrl}
                                              alt={system.title}
                                            />
                                          ) : (
                                            <span className="rdap-home-system-fallback">
                                              {system.initials}
                                            </span>
                                          )}
                                          <span className="rdap-home-system-title">
                                            {system.title}
                                          </span>
                                          {system.lifecycle ? (
                                            <span className="rdap-home-system-lifecycle">
                                              {system.lifecycle}
                                            </span>
                                          ) : null}
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};
